import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  Alert,
  Button,
  Center,
  Checkbox,
  createStyles,
  Group,
  LoadingOverlay,
  Paper,
  Stack,
  Title,
  TypographyStylesProvider,
  useMantineTheme,
} from '@mantine/core';
import { IconDownload, IconExclamationCircle } from '@tabler/icons-react';
import dayjs from 'dayjs';
import useDocument from 'hooks/document/useDocument';
import useSignDocument from 'hooks/document/useSignDocument';
import { useTracking } from 'hooks/mixpanel/useTracking';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { openDocumentDownload } from 'utils/documentDownload';
import notificationHandler from 'utils/notifications';

const useStyles = createStyles(theme => ({
  sigPad: {
    height: 140,
    width: '100%',
    borderRadius: theme.radius.lg,
  },
}));

const DocumentDetail = (): JSX.Element => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { classes } = useStyles();
  const { documentId } = useParams<{ documentId: string }>();

  const { data, isLoading, isError, isSuccess } = useDocument(documentId);
  const [contentsWithCheckboxes, setContentsWithCheckboxes] = useState<JSX.Element>(<></>);
  const [numberOfCheckBoxes, setNumberOfCheckBoxes] = useState(0);
  const [numberOfCheckedCheckBoxes, setNumberOfCheckedCheckBoxes] = useState(0);
  const signMutation = useSignDocument();
  const tracking = useTracking();

  const hasSignedDate = !!data?.data.signedDate;

  const sigRef = useRef<SignatureCanvas>(null);
  const [isSigned, setIsSigned] = useState(false);

  const handleClear = (): void => {
    sigRef.current?.clear();
    setIsSigned(false);
  };

  const handleDownloadPdf = (): void => {
    if (data?.data && isSuccess && data.data.hasPdf) {
      openDocumentDownload(data.data.documentId, 'signed');
    }
  };

  const handleAgreementCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (event.target.checked) {
        setNumberOfCheckedCheckBoxes(numberOfCheckedCheckBoxes + 1);
      } else {
        setNumberOfCheckedCheckBoxes(numberOfCheckedCheckBoxes - 1);
      }
    },
    [numberOfCheckedCheckBoxes],
  );

  const returnContentsWithAgreementCheckboxes = useCallback(
    (paragraphs: string[]): JSX.Element => {
      return (
        <>
          {paragraphs.map((paragraph, index) => (
            <>
              <>
                <TypographyStylesProvider key={index}>
                  <div dangerouslySetInnerHTML={{ __html: paragraph }} />
                </TypographyStylesProvider>
                {index !== paragraphs.length - 1 && (
                  <Checkbox
                    required
                    label='I have read and accept the text above.'
                    name='agreement'
                    value='false'
                    className='agreement-checkbox'
                    onChange={handleAgreementCheckboxChange}
                  />
                )}
              </>
            </>
          ))}
        </>
      );
    },
    [handleAgreementCheckboxChange],
  );

  useEffect(() => {
    if (data?.data) {
      var paragraphs = data.data.contents.split('REQUEST_ATHLETE_INITIALS');
      setNumberOfCheckBoxes(paragraphs.length - 1);
      setContentsWithCheckboxes(returnContentsWithAgreementCheckboxes(paragraphs));
    }
  }, [data?.data, returnContentsWithAgreementCheckboxes]);

  const handleSubmit = (): void => {
    if (sigRef.current && data?.data && isSuccess && numberOfCheckedCheckBoxes === numberOfCheckBoxes) {
      const { successNotification, errorNotification } = notificationHandler();

      const payload = {
        documentId: data.data.documentId,
        signature: sigRef.current.toDataURL(),
        signedDate: dayjs(new Date()).format('YYYY-MM-DDTHH:mm:ss'),
      };

      signMutation.mutate(payload, {
        onSuccess: response => {
          successNotification(
            'Document signed successfully.',
            `${response.data.description} successfully signed.`,
          );

          // Track document signing
          tracking.trackDocumentSign(payload.documentId, response.data.description || data.data.description);

          navigate('/documents', { replace: true });
        },
        onError: error => {
          errorNotification('Document sign failed.', error.response?.data.message);
        },
        onSettled: () => {
          handleClear();
        },
      });
    }
  };

  return (
    <Stack p='md' spacing='md' w='100%' h='100%' sx={{ overflowY: 'auto' }}>
      <LoadingOverlay visible={isLoading} />

      {isError && (
        <Center sx={{ flexGrow: 1 }} p='lg'>
          <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
            Something went wrong while fetching data!
          </Alert>
        </Center>
      )}

      {data?.data && isSuccess && (
        <>
          <Title order={4}>{data.data.description}</Title>
          <Paper p='sm' shadow='sm' radius='lg'>
            {contentsWithCheckboxes}
          </Paper>
          <Paper p='sm' shadow='sm' radius='lg'>
            <Stack spacing='sm'>
              <Title order={5}>Signature</Title>
              {hasSignedDate ? (
                data.data.signature && <img src={data.data.signature} className={classes.sigPad} />
              ) : (
                <SignatureCanvas
                  canvasProps={{ className: classes.sigPad }}
                  backgroundColor={theme.colors.gray[2]}
                  ref={sigRef}
                  onEnd={() => setIsSigned(true)}
                />
              )}
            </Stack>
          </Paper>

          {hasSignedDate ? (
            <Button
              variant='light'
              color='yellow'
              leftIcon={<IconDownload size={theme.fontSizes.lg} />}
              onClick={handleDownloadPdf}
            >
              Download PDF
            </Button>
          ) : (
            <Group noWrap grow>
              <Button disabled={!isSigned} color='red' onClick={handleClear}>
                Clear Signature
              </Button>
              <Button
                disabled={!isSigned || isLoading || numberOfCheckedCheckBoxes !== numberOfCheckBoxes}
                loading={signMutation.isLoading}
                onClick={handleSubmit}
                color='green'
              >
                Execute
              </Button>
            </Group>
          )}
        </>
      )}
    </Stack>
  );
};

export default DocumentDetail;
